/**
 * Cards under an Ask Clarid answer: actions the agent proposed and data fixes it found (step 2), and
 * engagement plans drafted from a SOW (step 3, EngagementCard).
 * The agent never acts on its own; each card waits for a person's click, and the server checks that
 * person's permission and records the decision in the audit log before anything happens.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  PlayIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  ChartBarIcon,
  TableCellsIcon,
  MapIcon,
  PresentationChartBarIcon,
} from '@heroicons/react/24/outline';
import { agentApi, type AgentCardRef, type ActionCardState, type DataFixCardState, type CardState } from '../../lib/agent';
import { shell, primaryBtn, secondaryBtn, useRefresh } from './cardKit';
import EngagementCard from './EngagementCard';
import DeliverableSignoff from './DeliverableSignoff';
import LoadCard from './LoadCard';
import ClientReportCard from './ClientReportCard';
import { RecordRecommendationsCard, RecommendationsTracker } from './RecommendationsCards';

export default function AgentCards({ teamId, refs }: { teamId: string; refs: AgentCardRef[] }) {
  const key = ['agent-cards', teamId, refs.map((r) => r.id).join(',')];
  const { data } = useQuery({
    queryKey: key,
    queryFn: () => agentApi.cards(teamId, refs).then((r) => r.data.cards),
    // A running analysis is finished by the server when its card is next read.
    refetchInterval: (q) => ((q.state.data as CardState[] | undefined)?.some((c) => c.status === 'RUNNING') ? 5000 : false),
  });
  if (!data?.length && !refs.some((r) => r.kind === 'recommendations')) return null;
  // Keep the order the agent proposed them in.
  const byId = new Map((data ?? []).map((c) => [c.id, c]));
  return (
    <div className="mt-3 space-y-2">
      {refs.map((r) => {
        // The tracker reads itself.
        if (r.kind === 'recommendations') return <RecommendationsTracker key={`rec-${r.id}`} teamId={teamId} includeClosed={r.id === 'all'} />;
        const card = byId.get(r.id);
        if (!card) return null;
        if (card.kind === 'engagement') return <EngagementCard key={card.id} teamId={teamId} card={card} queryKey={key} />;
        if (card.kind === 'deliverable') return <DeliverableSignoff key={card.id} teamId={teamId} deliverableId={card.id} />;
        if (card.kind === 'action' && card.type === 'load_file') return <LoadCard key={card.id} teamId={teamId} card={card} queryKey={key} />;
        if (card.kind === 'action' && card.type === 'record_recommendations') return <RecordRecommendationsCard key={card.id} teamId={teamId} card={card} queryKey={key} />;
        if (card.kind === 'action' && card.type === 'export_client_report') return <ClientReportCard key={card.id} teamId={teamId} card={card} queryKey={key} />;
        return card.kind === 'action'
          ? <ActionCard key={card.id} teamId={teamId} card={card} queryKey={key} />
          : <DataFixCard key={card.id} teamId={teamId} card={card} queryKey={key} />;
      })}
    </div>
  );
}

function ActionCard({ teamId, card, queryKey }: { teamId: string; card: ActionCardState; queryKey: unknown[] }) {
  const refresh = useRefresh(queryKey);
  const isWorkpaper = card.type === 'export_file_review';
  const isMaps = card.type === 'export_aa_maps';
  const isDeck = card.type === 'export_board_deck';
  const isProduced = card.type === 'produce';
  const isExport = card.type === 'export_fld_word' || isWorkpaper || isMaps || isDeck || isProduced;
  const download = async (path: string) => {
    try {
      await agentApi.download(path, isWorkpaper ? 'File_Review_Workpaper.xlsx' : isMaps ? 'Assessment_Area_Maps.docx' : isDeck ? 'Board_Deck.pptx' : isProduced ? 'Clarid_Report' : 'Fair_Lending_FLD.docx');
    } catch {
      // The API layer shows the error.
    }
  };
  const approve = useMutation({
    mutationFn: () => agentApi.approveAction(teamId, card.id).then((r) => r.data.card),
    onSuccess: async (next) => {
      await refresh();
      if (isExport && next.result?.downloadPath) await download(next.result.downloadPath);
    },
    // The API layer shows the error; refresh in case the card moved on.
    onError: refresh,
  });
  const reject = useMutation({
    mutationFn: () => agentApi.rejectAction(teamId, card.id),
    onSuccess: refresh,
  });

  const Icon = isWorkpaper ? TableCellsIcon : isMaps ? MapIcon : isDeck ? PresentationChartBarIcon : isExport ? DocumentTextIcon : ChartBarIcon;
  return (
    <div className={clsx(shell, card.status === 'FAILED' ? 'border-red-200' : 'border-gray-200')}>
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-ocean-600" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-navy-900">{card.title}</p>
          {card.status === 'PROPOSED' && (
            <>
              <p className="mt-0.5 text-xs text-gray-500">
                {isExport
                  ? isProduced
                    ? 'Built by the same code as its page, then stored for sign-off. Anything it leaves out is listed here.'
                    : isDeck
                    ? 'For the board: fair lending results, the Focal Points, open recommendations, assessment area maps and sign-off status. Every slide names its source.'
                    : isMaps
                    ? 'One map per active assessment area: majority-minority and low- or moderate-income tracts from Clarid’s Census tables, with the branches.'
                    : isWorkpaper
                    ? 'For each Focal Point: the IFLEP sample sizes and every candidate file with its decision factors side by side. You choose the sample; Clarid does not.'
                    : 'Includes the evidence appendix: source files and their fingerprints, data readiness, and how to verify the copy.'
                  : 'Runs the full analysis with the default settings, the same as running it from the Fair Lending page. Your approval is recorded.'}
              </p>
              <div className="mt-2 flex gap-2">
                <button type="button" className={primaryBtn} disabled={approve.isPending || reject.isPending} onClick={() => approve.mutate()}>
                  {isExport ? <ArrowDownTrayIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                  {approve.isPending ? (isExport ? 'Preparing…' : 'Starting…') : isExport ? 'Download' : 'Run'}
                </button>
                <button type="button" className={secondaryBtn} disabled={approve.isPending || reject.isPending} onClick={() => reject.mutate()}>
                  Not now
                </button>
              </div>
            </>
          )}
          {card.status === 'RUNNING' && (
            <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-gray-600">
              <ArrowPathIcon className="h-4 w-4 animate-spin text-ocean-600" /> Running. This usually takes a minute or two.
            </p>
          )}
          {card.status === 'DONE' && !isExport && card.result?.analysisId && (
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <CheckCircleIcon className="h-4 w-4" /> Completed{card.analysis?.overallRiskLevel ? ` · overall risk ${titleCase(card.analysis.overallRiskLevel)}` : ''}
              </span>
              <Link to={`/fair-lending/fld/${card.result.analysisId}`} className="font-medium text-ocean-700 hover:text-ocean-800">
                View the results
              </Link>
            </p>
          )}
          {card.status === 'DONE' && isExport && card.result?.deliverableId && (
            <DeliverableSignoff teamId={teamId} deliverableId={card.result.deliverableId} />
          )}
          {card.status === 'DONE' && !!card.result?.notes?.length && (
            <ul className="mt-2 ml-5 list-disc text-xs text-amber-800">{card.result.notes.map((n) => <li key={n}>{n}</li>)}</ul>
          )}
          {card.status === 'DONE' && isExport && !card.result?.deliverableId && card.result?.downloadPath && (
            <button type="button" className={clsx(secondaryBtn, 'mt-2')} onClick={() => download(card.result!.downloadPath!)}>
              <ArrowDownTrayIcon className="h-4 w-4" /> Download again
            </button>
          )}
          {card.status === 'FAILED' && (
            <p className="mt-1 inline-flex items-center gap-1 text-xs text-red-700">
              <ExclamationTriangleIcon className="h-4 w-4" /> {card.error ?? 'It did not finish.'}
            </p>
          )}
          {card.status === 'REJECTED' && <p className="mt-1 text-xs text-gray-500">Not run.</p>}
        </div>
      </div>
    </div>
  );
}

function DataFixCard({ teamId, card, queryKey }: { teamId: string; card: DataFixCardState; queryKey: unknown[] }) {
  const refresh = useRefresh(queryKey);
  const [note, setNote] = useState('');
  const approve = useMutation({
    mutationFn: () => agentApi.approveFix(teamId, card.id, note.trim() || undefined),
    onSuccess: refresh,
    // The API layer shows the error (e.g. the data moved); the card then shows Superseded.
    onError: refresh,
  });
  const reject = useMutation({
    mutationFn: () => agentApi.rejectFix(teamId, card.id, note.trim() || undefined),
    onSuccess: refresh,
  });
  const groups = card.preview?.groups ?? [];
  const shown = groups.slice(0, 5);
  const busy = approve.isPending || reject.isPending;

  return (
    <div className={clsx(shell, 'border-gray-200')}>
      <div className="flex items-start gap-3">
        <WrenchScrewdriverIcon className="mt-0.5 h-5 w-5 shrink-0 text-ocean-600" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-navy-900">{card.title}</p>
          {shown.length > 0 && (
            <table className="mt-2 w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1 pr-3 font-medium">Before</th>
                  <th className="py-1 pr-3 font-medium">After</th>
                  <th className="py-1 text-right font-medium">Values</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {shown.map((g, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="py-1 pr-3 text-red-700">{g.before}</td>
                    <td className="py-1 pr-3 text-emerald-700">{g.after}</td>
                    <td className="py-1 text-right font-sans text-gray-600">{g.count.toLocaleString('en-US')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {groups.length > shown.length && (
            <p className="mt-1 text-xs text-gray-500">and {(groups.length - shown.length).toLocaleString('en-US')} more distinct values</p>
          )}
          {!!card.preview?.notChangedCount && (
            <p className="mt-1 text-xs text-amber-700">
              {card.preview.notChangedCount.toLocaleString('en-US')} value(s) Clarid cannot read are left as reported.
            </p>
          )}

          {card.status === 'PROPOSED' && (
            <>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 500))}
                placeholder="Note for the record (optional)"
                className="mt-2 w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-ocean-500 focus:outline-none focus:ring-1 focus:ring-ocean-500"
              />
              <div className="mt-2 flex gap-2">
                <button type="button" className={primaryBtn} disabled={busy} onClick={() => approve.mutate()}>
                  <CheckCircleIcon className="h-4 w-4" /> {approve.isPending ? 'Applying…' : 'Approve and apply'}
                </button>
                <button type="button" className={secondaryBtn} disabled={busy} onClick={() => reject.mutate()}>
                  <XCircleIcon className="h-4 w-4" /> Reject
                </button>
              </div>
              <p className="mt-1.5 text-xs text-gray-400">Owners and Compliance Officers can approve. Clarid re-checks the data first and applies exactly this change.</p>
            </>
          )}
          {card.status === 'APPLIED' && (
            <p className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-700">
              <CheckCircleIcon className="h-4 w-4" /> Applied to {(card.appliedCount ?? card.changeCount).toLocaleString('en-US')} value(s). Re-run the analysis so reports reflect it.
            </p>
          )}
          {card.status === 'REJECTED' && <p className="mt-1 text-xs text-gray-500">Rejected. Nothing changed.</p>}
          {card.status === 'SUPERSEDED' && (
            <p className="mt-1 text-xs text-amber-700">The data changed after this was proposed, so it was not applied. Ask Clarid to look again.</p>
          )}
          {card.decisionNote && <p className="mt-1 text-xs italic text-gray-500">Note: {card.decisionNote}</p>}
        </div>
      </div>
    </div>
  );
}

const titleCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();
